/**
 * The review case's PR-side read (YAG-440): the same
 * `CodeReviewPrReviewsResponse` the PR Reviews modal reads, addressed by the
 * case work item. The gate, findings, runs, and activity reads stay the
 * existing work-item hooks — this hook carries only what is new.
 *
 * A 404 is a STATE, not an error: a predecessor case (closed-then-reopened
 * PR, ADR-0032) or a case whose pointer healed away resolves to `prMissing`,
 * and the surface hides its PR sections instead of erroring. The identity
 * rule: `prStateId` never becomes component state — it rides inside the
 * response and is spent only on the re-run POST.
 *
 * Poll mechanics mirror `usePrStateReviews` (workers/useWorkers.ts): a silent
 * 10s tick while a report is in progress or a re-run is armed; a failed tick
 * keeps the last good data.
 */
import { useCallback, useEffect, useState } from "react";
import type { CodeReviewPrReviewsResponse } from "@yagni/shared";
import { captureUiError } from "../../sentry.js";

/** ~10 s between silent polls while a review is running for the open surface. */
const REVIEW_POLL_MS = 10_000;

export interface UseReviewCasePrResult {
  data: CodeReviewPrReviewsResponse | null;
  /** The case has no watched PR pointing at it (predecessor case) — hide, don't error. */
  prMissing: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /**
   * "Re-run review": a state write the sweep picks up. A 409's body text is
   * the honest refusal the surface prints verbatim — never a pretend success.
   */
  rerun: () => Promise<{ ok: boolean; message: string }>;
  /**
   * "Review again" on a merged or closed pull request: the Team's own manual
   * review request, which the re-arm behind `rerun` refuses once the pull
   * request is off the watch. A refusal's body text is printed verbatim.
   */
  requestAgain: (deskId: string) => Promise<{ ok: boolean; message: string }>;
}

export function useReviewCasePr(workItemId: string | null): UseReviewCasePrResult {
  const [data, setData] = useState<CodeReviewPrReviewsResponse | null>(null);
  const [prMissing, setPrMissing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  // Armed by a queued re-run: the rearm leaves NO in-progress report until
  // the next sweep claims the PR, so the poll must run on the arm alone.
  const [rerunArmed, setRerunArmed] = useState(false);

  const url = workItemId
    ? `/api/code-review/work-items/${encodeURIComponent(workItemId)}/reviews`
    : null;

  const refetch = useCallback(async () => {
    if (!url) {
      setData(null);
      setPrMissing(false);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await fetch(url, { credentials: "include" });
      if (res.status === 404) {
        setData(null);
        setPrMissing(true);
        setError(null);
        return;
      }
      if (!res.ok) throw new Error(`Reviews load failed (${res.status})`);
      setData((await res.json()) as CodeReviewPrReviewsResponse);
      setPrMissing(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("Reviews load failed"));
    } finally {
      setIsLoading(false);
    }
  }, [url]);

  useEffect(() => {
    setRerunArmed(false);
    void refetch();
  }, [refetch]);

  /**
   * The poll is silent: no isLoading flip (the surface must not flash every
   * tick) and a failed tick keeps the last good data — the next tick tries
   * again rather than swapping in the error view mid-read.
   */
  const silentRefetch = useCallback(async () => {
    if (!url) return;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return;
      setData((await res.json()) as CodeReviewPrReviewsResponse);
      setError(null);
    } catch {
      // Kept: the last good data. A transient poll failure is not an error state.
    }
  }, [url]);

  const hasInProgress = data?.reviews.some((review) => review.status === "in_progress") ?? false;

  // Once the sweep claims the re-run, the in-progress report carries the
  // polling and the arm has done its job — so a later terminal status stops it.
  useEffect(() => {
    if (rerunArmed && hasInProgress) setRerunArmed(false);
  }, [rerunArmed, hasInProgress]);

  useEffect(() => {
    if (!url || (!hasInProgress && !rerunArmed)) return;
    const timer = setInterval(() => void silentRefetch(), REVIEW_POLL_MS);
    return () => clearInterval(timer);
  }, [url, hasInProgress, rerunArmed, silentRefetch]);

  const rerun = useCallback(async (): Promise<{ ok: boolean; message: string }> => {
    const prStateId = data?.pr.prStateId;
    if (!prStateId) return { ok: false, message: "" };
    try {
      const res = await fetch(
        `/api/code-review/pr-state/${encodeURIComponent(prStateId)}/rerun`,
        { method: "POST", credentials: "include" },
      );
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        return { ok: false, message: body?.error || `Could not re-run (${res.status})` };
      }
      setRerunArmed(true);
      await refetch();
      return { ok: true, message: "" };
    } catch {
      return { ok: false, message: "Could not re-run just now." };
    }
  }, [data?.pr.prStateId, refetch]);

  const requestAgain = useCallback(
    async (deskId: string): Promise<{ ok: boolean; message: string }> => {
      const watched = data?.pr;
      if (!watched) {
        return {
          ok: false,
          message: "This case no longer names a pull request, so there is nothing to ask for.",
        };
      }
      try {
        const res = await fetch(
          `/api/code-review/desks/${encodeURIComponent(deskId)}/review-requests`,
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ input: watched.prUrl, repo: watched.repoFullName }),
          },
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          return { ok: false, message: body?.error || `Could not ask for a review (${res.status})` };
        }
        // The review lands on its own case (a terminal case stays terminal),
        // so this read does not poll for it; the Work list picks it up.
        return { ok: true, message: "" };
      } catch (err) {
        // A thrown fetch is the network, not a refusal: the route's own
        // sentences come back above with a status. Say so, and record it.
        captureUiError(err, {
          area: "work",
          operation: "review_again_request",
          workItemId: watched.workItemId ?? undefined,
        });
        return { ok: false, message: "Could not reach the server to ask for a review. Try again." };
      }
    },
    [data?.pr],
  );

  return { data, prMissing, isLoading, error, refetch, rerun, requestAgain };
}
